import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  User,
  CreditCard,
  MapPin,
  Phone,
  CheckCircle,
  Clock,
  Shield,
  Heart
} from 'lucide-react';
import EnrollmentChart from '../components/EnrollmentChart';

const Enroll = () => {
  const eligibilityRequirements = [
    "Child must be aged 2-18 years",
    "Child must be enrolled in an AMHAS provider school",
    "Parent/guardian must provide valid identification",
    "Child must have birth certificate or valid ID",
    "Parent/guardian must provide contact information"
  ];

  const requiredDocuments = [
    "Child's birth certificate or valid ID",
    "Parent/guardian identification",
    "Proof of school enrollment",
    "Contact information (phone, email, address)",
    "Emergency contact details"
  ];

  const paymentOptions = [
    {
      method: "Cash",
      description: "Pay at school during enrollment sessions",
      availability: "All regions"
    },
    {
      method: "Mobile Money",
      description: "MTN Mobile Money, Orange Money",
      availability: "Urban and rural areas"
    },
    {
      method: "Bank Transfer",
      description: "Direct bank transfer to AMHAS account",
      availability: "All regions"
    }
  ];

  const enrollmentSteps = [
    {
      step: "1",
      title: "Complete Application",
      description: "Fill out the enrollment form with child and parent information"
    },
    {
      step: "2",
      title: "Submit Documents",
      description: "Provide required documents and identification"
    },
    {
      step: "3",
      title: "Pay Premium",
      description: `Pay annual premium: $28 USD (15,820 CFA / 2,030 GMD) - one price for all families`
    },
    {
      step: "4",
      title: "Receive ID Card",
      description: "Get your child's digital health ID card"
    },
    {
      step: "5",
      title: "Coverage Begins",
      description: "At school: vitals & symptoms entered → AI pre‑diagnosis → Western doctor review. Clinics only if procedures are required."
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary-600 to-accent-600 text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Enroll Your Child</h1>
            <p className="text-xl md:text-2xl text-primary-100">
              Simple steps to protect your child's health
            </p>
          </motion.div>
        </div>
      </section>

      {/* Eligibility */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Who Can Enroll?</h2>
            <p className="section-subtitle">
              Eligibility requirements for AMHAS enrollment
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Eligibility Requirements</h3>
              <ul className="space-y-4">
                {eligibilityRequirements.map((requirement, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{requirement}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Required Documents</h3>
              <ul className="space-y-4">
                {requiredDocuments.map((document, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{document}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Enrollment Process */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Enrollment Process</h2>
            <p className="section-subtitle">
              Simple steps to get your child enrolled
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            {enrollmentSteps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  {step.step}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-600 text-sm">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Payment Options */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Payment Options</h2>
            <p className="section-subtitle">
              Flexible payment methods available in your region
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {paymentOptions.map((option, index) => (
              <motion.div
                key={option.method}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card p-8 text-center"
              >
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CreditCard size={32} className="text-primary-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{option.method}</h3>
                <p className="text-gray-600 mb-4">{option.description}</p>
                <div className="text-sm text-primary-600 font-semibold">{option.availability}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* After Enrollment */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">After Enrollment</h2>
            <p className="section-subtitle">
              What happens next and how to use your coverage
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="card p-8 text-center"
            >
              <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield size={32} className="text-accent-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Receive ID Card</h3>
              <p className="text-gray-600">
                Your child will receive a digital health ID card within 7 days of enrollment.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="card p-8 text-center"
            >
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Clock size={32} className="text-primary-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Coverage Starts</h3>
              <p className="text-gray-600">
                Coverage begins immediately upon enrollment and payment confirmation.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="card p-8 text-center"
            >
              <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart size={32} className="text-secondary-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Access Care</h3>
              <p className="text-gray-600">
                Start with AI + telehealth from school. If procedures are required, you’ll be referred to a provider clinic and use your ID card.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Enrollment Growth Chart */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <EnrollmentChart />
          </motion.div>
        </div>
      </section>

      {/* Support */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Support & Assistance</h2>
            <p className="section-subtitle">
              We're here to help you through the enrollment process
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="card p-8 text-center"
            >
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Phone size={32} className="text-primary-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Hotline Support</h3>
              <p className="text-gray-600 mb-4">Call our support hotline for immediate assistance</p>
              <div className="text-primary-600 font-semibold">+220 404 0966</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="card p-8 text-center"
            >
              <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <User size={32} className="text-accent-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Office Hours</h3>
              <p className="text-gray-600 mb-4">Visit our offices for in-person support</p>
              <div className="text-accent-600 font-semibold">Mon-Fri: 8AM-5PM</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="card p-8 text-center"
            >
              <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <MapPin size={32} className="text-secondary-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Regional Support</h3>
              <p className="text-gray-600 mb-4">Find support centers in your region</p>
              <Link to="/contact" className="text-secondary-600 font-semibold hover:underline">
                Find Support Center
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary-600 text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-4">Ready to Enroll?</h2>
            <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
              Protect your child's health today. Join thousands of families who have already enrolled their children in AMHAS.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <Link to="/student-enrollment" className="btn-secondary">
                Start Enrollment
              </Link>
              <a href="https://www.google.com/maps/search/schools+near+me" target="_blank" rel="noopener noreferrer" className="btn-secondary">
                Find Your School
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Enroll;
